import type { CoverageDeductibleDiscount } from '@/services/coverages/types';

/**
 * Retorna o desconto da franquia aplicável ao SI (LMI) informado — a faixa
 * cujo intervalo [siMin, siMax] contém o valor. `undefined` quando não há
 * desconto para o SI (ou a franquia não concede desconto).
 */
export function findDeductibleDiscountForSi(
  discounts: CoverageDeductibleDiscount[] | null | undefined,
  siValue: number
): CoverageDeductibleDiscount | undefined {
  if (!discounts || discounts.length === 0 || siValue <= 0) return undefined;
  return discounts.find(
    discount =>
      (discount.siMin == null || siValue >= discount.siMin) &&
      (discount.siMax == null || siValue <= discount.siMax)
  );
}

/**
 * Formata o percentual de desconto para exibição em pt-BR.
 * "0.25%" → "0,25% de desconto". Valores fora do padrão são exibidos como
 * vieram do backend, apenas com o sufixo.
 */
export function formatDeductibleDiscountLabel(value: string): string {
  const trimmed = String(value ?? '').trim();
  if (!trimmed) return '';
  const numeric = Number(trimmed.replace('%', '').trim());
  if (Number.isNaN(numeric)) return `${trimmed} de desconto`;
  const formatted = numeric.toLocaleString('pt-BR', {
    maximumFractionDigits: 2,
  });
  return `${formatted}% de desconto`;
}

/**
 * Monta o label da opção de franquia no seletor, anexando o desconto quando
 * há faixa aplicável ao SI informado: "<franquia> - x% de desconto".
 */
export function getDeductibleOptionLabelWithDiscount(
  baseLabel: string,
  discounts: CoverageDeductibleDiscount[] | null | undefined,
  siValue: number
): string {
  const discount = findDeductibleDiscountForSi(discounts, siValue);
  if (!discount) return baseLabel;
  const discountLabel = formatDeductibleDiscountLabel(discount.value);
  if (!discountLabel) return baseLabel;
  return `${baseLabel} - ${discountLabel}`;
}
